import React from "react";

const TYPE_OPTIONS = [
    { value: "web", label: "Web" },
    { value: "pdf", label: "PDF" },
    { value: "doc", label: "DOC / Word" },
    { value: "sheet", label: "XLS / CSV" },
    { value: "image", label: "Image" },
    { value: "map", label: "Carte (Google Maps)" },
    { value: "drive", label: "Google Drive" },
];

const SectionCard = ({ title, icon, children }) => (
    <div className="bg-white shadow-lg p-4 md:p-6 rounded-lg">
        <div className="flex items-center gap-2 mb-4">
            <div className="flex justify-center items-center bg-orange-600 rounded-lg w-9 h-9 text-white">
                <i className={`fa-solid ${icon}`} />
            </div>
            <h5 className="font-semibold text-gray-800">{title}</h5>
        </div>
        {children}
    </div>
);

export default function ExternalLinksCard({
    lstLiensExternes = [],
    title = "Liens externes",
    icon = "fa-up-right-from-square",
    maxWithoutScroll = 5,
    editable = true,

    loading = true,
    mutating = false,
    defaultUUIDExterne = null,
    defaultOrigine = "",

    onCreate,
    onUpdate,
    onDelete,
    onBulkSave,
    onChange,
}) {
    const [q, setQ] = React.useState("");
    const [links, setLinks] = React.useState(() => toObjectsDB(lstLiensExternes));
    const [openForm, setOpenForm] = React.useState(false);
    const [editingId, setEditingId] = React.useState(null);
    const [form, setForm] = React.useState(blankForm());
    const [confirmDel, setConfirmDel] = React.useState({ open: false, link: null });
    const [localError, setLocalError] = React.useState("");
    const [openAllReport, setOpenAllReport] = React.useState(null);

    const isDeclarationOrigin = normOri(defaultOrigine) === "declaration";
    const [declOnly, setDeclOnly] = React.useState(!isDeclarationOrigin);

    React.useEffect(() => {
        setLinks(toObjectsDB(lstLiensExternes));
    }, [lstLiensExternes]);

    const filtered = React.useMemo(() => {
        const base = (() => {
            if (!q.trim()) return links;
            const s = q.trim().toLowerCase();
            return links.filter((l) => {
                const domain = getDomain(l.Lien).toLowerCase();
                const nom = (l.Nom_Lien || "").toLowerCase();
                const lien = (l.Lien || "").toLowerCase();

                return (
                    nom.includes(s) ||
                    lien.includes(s) ||
                    domain.includes(s) ||
                    (l.Commentaires || "").toLowerCase().includes(s) ||
                    (l.Origine || "").toLowerCase().includes(s) ||
                    (l.Type_Lien || "").toLowerCase().includes(s)
                );
            });

        })();

        if (isDeclarationOrigin && declOnly) {
            return base.filter(
                (l) =>
                    normOri(l.Origine) === "declaration" &&
                    (!defaultUUIDExterne || l.UUID_Externe === defaultUUIDExterne)
            );
        }
        return base;
    }, [links, q, isDeclarationOrigin, declOnly, defaultUUIDExterne]);

    const shouldScroll = filtered.length > maxWithoutScroll;
    const isBusy = loading || mutating;

    function openAdd() {
        if (!editable || isBusy) return;
        setEditingId(null);
        setForm({
            ...blankForm(),
            UUID_Externe: defaultUUIDExterne,
            Origine: defaultOrigine,
        });
        setOpenForm(true);
        setLocalError("");
    }

    function openEdit(link) {
        if (!editable || isBusy) return;
        setEditingId(link.id);
        setForm({
            UUID_: link.UUID_ || null,
            UUID_Externe: link.UUID_Externe ?? defaultUUIDExterne,
            Nom_Lien: link.Nom_Lien || "",
            Lien: link.Lien || "",
            Commentaires: link.Commentaires || "",
            Partage: !!link.Partage,
            Origine: link.Origine ?? defaultOrigine,
            Type_Lien: normTypeKey(link.Type_Lien || guessType(link.Lien)),
            Creation: link.Creation || null,
        });
        setOpenForm(true);
        setLocalError("");
    }

    async function saveForm(e) {
        e?.preventDefault?.();
        setLocalError("");

        const clean = {
            id: editingId ?? genId(), // UI only
            UUID_: form.UUID_ || null,
            UUID_Externe: form.UUID_Externe ?? defaultUUIDExterne ?? null,
            Nom_Lien: (form.Nom_Lien || getDomain(form.Lien) || form.Lien || "").trim(),
            Lien: ensureHttp(form.Lien?.trim()),
            Commentaires: (form.Commentaires || "").trim(),
            Partage: !!form.Partage,
            Origine: (form.Origine ?? defaultOrigine ?? "").trim(),
            Type_Lien: (form.Type_Lien || guessType(form.Lien)).toString(),
            Creation: form.Creation || null,
        };

        if (!clean.Lien) {
            setLocalError("L’URL est requise.");
            return;
        }

        try {
            if (onCreate || onUpdate) {
                if (clean.UUID_) {
                    const server = (await onUpdate?.(toDB1(clean))) || toDB1(clean);
                    setLinks((prev) => prev.map((p) => (p.id === editingId ? toUI(server) : p)));
                } else {
                    const server = (await onCreate?.(toDB1(clean))) || toDB1(clean);
                    setLinks((prev) => [...prev, toUI(server)]);
                }
            } else if (onBulkSave) {
                const next = editingId ? links.map((p) => (p.id === editingId ? clean : p)) : [...links, clean];
                await onBulkSave(toDB(next));
                setLinks(next);
            } else if (onChange) {
                const next = editingId ? links.map((p) => (p.id === editingId ? clean : p)) : [...links, clean];
                onChange(toDB(next));
                setLinks(next);
            }

            setOpenForm(false);
            setEditingId(null);
        } catch (err) {
            setLocalError(err?.message || "Erreur lors de l’enregistrement.");
        }
    }

    function askDelete(link) {
        if (!editable || isBusy) return;
        setConfirmDel({ open: true, link });
    }

    async function confirmDelete() {
        const link = confirmDel.link;
        setLocalError("");

        try {
            if (onDelete) {
                await onDelete(link.UUID_);
                setLinks((prev) => prev.filter((p) => p.id !== link.id));
            } else if (onBulkSave) {
                const next = links.filter((p) => p.id !== link.id);
                await onBulkSave(toDB(next));
                setLinks(next);
            } else if (onChange) {
                const next = links.filter((p) => p.id !== link.id);
                onChange(toDB(next));
                setLinks(next);
            }
        } catch (err) {
            setLocalError(err?.message || "Suppression impossible.");
        } finally {
            setConfirmDel({ open: false, link: null });
        }
    }

    function cancelDelete() {
        setConfirmDel({ open: false, link: null });
    }

    return (
        <SectionCard title={title} icon={icon}>

            {isDeclarationOrigin && (
                <div class="flex">
                    <label class="flex gap-2 -ml-10 mcui-checkbox">
                        <input
                            type="checkbox"
                            checked={declOnly}
                            onChange={(e) => setDeclOnly(e.target.checked)}
                            disabled={isBusy}
                        />
                        <div>
                            <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                            </svg>
                        </div>
                        <span className="text-gray-500 text-lg">Masquer les liens externes hérités du Site</span>
                    </label>
                </div>
            )}

            {openAllReport && openAllReport.failed > 0 && (
                <div className="bg-amber-50 mb-3 px-3 py-2 border border-amber-200 rounded-md text-amber-800 text-sm">
                    <i className="mr-2 fa-solid fa-circle-info" />
                    {openAllReport.opened}/{openAllReport.total} ouvert(s). Votre navigateur a bloqué {openAllReport.failed} fenêtre(s).
                    <button
                        className="ml-2 underline"
                        onClick={async () => {
                            try {
                                const rest = filtered
                                    .map((l) => ensureHttp(l.Lien))
                                    .slice(openAllReport.opened);
                                await navigator.clipboard.writeText(rest.join("\n"));
                            } catch { }
                        }}
                    >
                        Copier les liens restants
                    </button>
                </div>
            )}

            {/* Erreurs locales */}
            {localError && (
                <div className="bg-red-50 mb-3 px-3 py-2 border border-red-200 rounded-md text-red-700 text-sm">
                    <i className="mr-2 fa-solid fa-circle-exclamation" />
                    {localError}
                </div>
            )}

            {/* Barre d’actions */}
            {loading ? (
                <BarSkeleton />
            ) : (
                <div className="flex items-center gap-2 mb-3">
                    <div className="relative flex-1">
                        <i className="top-1/2 left-3 absolute text-gray-400 text-xs -translate-y-1/2 pointer-events-none fa-solid fa-magnifying-glass" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Rechercher ..."
                            className="pr-8 pl-9 border border-gray-300 focus:border-orange-400 rounded-md outline-none focus:ring-2 focus:ring-orange-200 w-full h-9 text-sm transition"
                            disabled={isBusy}
                        />
                        {q && (
                            <button
                                type="button"
                                aria-label="Effacer"
                                className="top-1/2 right-2 absolute text-gray-400 hover:text-gray-600 -translate-y-1/2"
                                onClick={() => setQ("")}
                                disabled={isBusy}
                            >
                                <i className="fa-solid fa-xmark" />
                            </button>
                        )}
                    </div>

                    {editable && (
                        <button
                            type="button"
                            className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-50 px-3 py-2 border rounded-lg text-white text-sm transition-all duration-300"
                            onClick={openAdd}
                            disabled={isBusy}
                            title="Ajouter un lien"
                        >
                            <i className="fa-solid fa-plus" />
                            <span className="hidden sm:inline">Ajouter</span>
                        </button>
                    )}
                </div>
            )}

            {/* Liste */}
            {loading ? (
                <LinksSkeleton rows={Math.min(8, Math.max(4, lstLiensExternes?.length || 6))} />
            ) : links.length === 0 ? (
                <div className="flex justify-between items-center p-4 border border-dashed rounded-lg text-gray-500 text-sm">
                    {mutating ? "Mise à jour…" : "Aucun lien externe."}
                    <i className="fa-regular fa-face-smile" />
                </div>
            ) : filtered.length === 0 ? (
                <div
                    className={`p-4 border border-dashed rounded-lg text-gray-500 text-sm ${maxWithoutScroll < lstLiensExternes.length ? "h-72" : ""
                        }`}
                >
                    Aucun résultat pour « {q} ».
                </div>
            ) : (
                <ul
                    className={`divide-y ${maxWithoutScroll < lstLiensExternes.length ? "h-72 overflow-y-auto pr-1" : ""
                        }`}
                >
                    {filtered.map((l) => (
                        <li key={l.id} className="py-2">
                            <LinkRow
                                link={l}
                                onEdit={editable ? () => openEdit(l) : null}
                                onDelete={editable ? () => askDelete(l) : null}
                                busy={isBusy}
                            />
                        </li>
                    ))}
                </ul>
            )}

            {/* Modal formulaire */}
            {openForm && (
                <div className="z-50 fixed inset-0 place-items-center grid bg-black/40 p-4" onClick={() => (isBusy ? null : setOpenForm(false))}>
                    <div
                        className="bg-white shadow-xl p-5 border rounded-xl w-full max-w-lg origin-center transition transform"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-3">
                            <div className="flex items-center gap-2">
                                <span className="place-items-center grid bg-orange-600 rounded-lg w-8 h-8 text-white">
                                    <i className="fa-solid fa-link" />
                                </span>
                                <h3 className="font-semibold text-lg">{editingId ? "Modifier le lien" : "Ajouter un lien"}</h3>
                            </div>
                            <button className="hover:bg-gray-100 disabled:opacity-50 p-2 rounded-md text-gray-500" onClick={() => setOpenForm(false)} aria-label="Fermer" disabled={isBusy}>
                                <i className="fa-solid fa-xmark" />
                            </button>
                        </div>

                        <form onSubmit={saveForm} className="gap-3 grid">
                            <div className="gap-3 grid grid-cols-1 sm:grid-cols-2">
                                <div>
                                    <label className="text-gray-600 text-xs">Nom du Lien</label>
                                    <input
                                        className="mt-1 px-3 border focus:border-orange-400 rounded-md focus:ring-2 focus:ring-orange-200 w-full h-10 text-sm"
                                        value={form.Nom_Lien}
                                        onChange={(e) => setForm((f) => ({ ...f, Nom_Lien: e.target.value }))}
                                        placeholder="Ex. PV de réception"
                                        disabled={isBusy}
                                    />
                                </div>

                                {/* SELECT Type */}
                                <div>
                                    <label className="text-gray-600 text-xs">Type de Lien</label>
                                    <select
                                        className="bg-white mt-1 px-3 border focus:border-orange-400 rounded-md focus:ring-2 focus:ring-orange-200 w-full h-10 text-sm"
                                        value={normTypeKey(form.Type_Lien || "")}
                                        onChange={(e) => setForm((f) => ({ ...f, Type_Lien: e.target.value }))}
                                        disabled={isBusy}
                                    >
                                        {TYPE_OPTIONS.map((opt) => (
                                            <option value={opt.value} key={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="text-gray-600 text-xs">
                                    Lien <span className="text-red-500">*</span>
                                </label>
                                <input
                                    required
                                    className="mt-1 px-3 border focus:border-orange-400 rounded-md focus:ring-2 focus:ring-orange-200 w-full h-10 font-mono text-sm"
                                    value={form.Lien}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setForm((f) => {
                                            const prevGuess = normTypeKey(f.Type_Lien || "");
                                            const newGuess = normTypeKey(guessType(val));
                                            // Auto-guess si l'utilisateur n'a pas explicitement choisi un autre type
                                            const nextType = (!f.Type_Lien || f.Type_Lien === prevGuess) ? newGuess : f.Type_Lien;
                                            return { ...f, Lien: val, Type_Lien: nextType };
                                        });
                                    }}
                                    placeholder="https://exemple.com/doc.pdf"
                                    disabled={isBusy}
                                />
                                <p className="mt-1 text-gray-400 text-xs">http(s) ajouté automatiquement si besoin</p>
                            </div>

                            <div>
                                <label className="text-gray-600 text-xs">Commentaires</label>
                                <input
                                    className="mt-1 px-3 border focus:border-orange-400 rounded-md focus:ring-2 focus:ring-orange-200 w-full h-10 text-sm"
                                    value={form.Commentaires}
                                    onChange={(e) => setForm((f) => ({ ...f, Commentaires: e.target.value }))}
                                    placeholder="ex. Version signée du 21/03/2025"
                                    disabled={isBusy}
                                />
                            </div>

                            <label className="inline-flex items-center gap-2 text-sm">
                                <label class="gap-2 -ml-4 min-w-36 text-right mcui-checkbox">
                                    <input
                                        type="checkbox"
                                        checked={!!form.Partage}
                                        onChange={(e) => setForm((f) => ({ ...f, Partage: e.target.checked }))}
                                        disabled={isBusy}
                                        class="text-gray-900"
                                    />
                                    <div>
                                        <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                            <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                        </svg>
                                    </div>
                                    <span className="text-gray-900 text-lg">Partage</span>
                                </label>

                            </label>

                            <div className="flex justify-end gap-2 mt-2">
                                <button type="button" className="hover:bg-gray-50 disabled:opacity-50 px-3 py-2 border rounded-lg text-sm" onClick={() => setOpenForm(false)} disabled={isBusy}>
                                    Annuler
                                </button>
                                <button type="submit" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 px-3 py-2 rounded-lg font-medium text-white text-sm" disabled={isBusy}>
                                    <i className="fa-solid fa-floppy-disk" />
                                    Enregistrer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal confirmation suppression */}
            {confirmDel.open && (
                <div className="z-50 fixed inset-0 place-items-center grid bg-black/40 p-4" onClick={cancelDelete} aria-modal="true" role="dialog">
                    <div className="bg-white shadow-xl p-5 border rounded-xl w-full max-w-md origin-center transition transform" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start gap-3">
                            <div className="place-items-center grid bg-red-100 rounded-lg w-10 h-10 text-red-600">
                                <i className="fa-solid fa-triangle-exclamation" />
                            </div>
                            <div className="min-w-0">
                                <h3 className="font-semibold text-gray-900 text-base">Supprimer ce lien ?</h3>
                                <p className="mt-1 text-gray-600 text-sm">
                                    {confirmDel.link?.Nom_Lien || getDomain(confirmDel.link?.Lien) || confirmDel.link?.Lien}
                                </p>
                                <p className="mt-2 text-gray-500 text-xs">Cette action est irréversible. Le lien sera retiré de la liste.</p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-5">
                            <button className="hover:bg-gray-50 px-3 py-2 border rounded-lg text-sm" onClick={cancelDelete} disabled={isBusy}>
                                Annuler
                            </button>
                            <button className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 px-3 py-2 rounded-lg font-medium text-white text-sm" onClick={confirmDelete} disabled={isBusy}>
                                <i className="fa-regular fa-trash-can" />
                                Supprimer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </SectionCard>
    );
}

function LinkRow({ link, onEdit, onDelete, busy }) {
    const domain = getDomain(link.Lien);
    const type = (link.Type_Lien || guessType(link.Lien)).toLowerCase();

    return (
        <div className="group flex justify-between items-center gap-3 rounded-md transition">
            <a
                href={ensureHttp(link.Lien)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-1 items-center gap-3 ml-3 min-w-0"
                title={link.Lien}
            >
                <img
                    src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`}
                    alt=""
                    className="rounded-sm w-5 h-5"
                    loading="lazy"
                />
                <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{link.Nom_Lien || domain || link.Lien}</div>
                    {link.Commentaires ? (
                        <div className="text-gray-500 text-xs truncate">
                            {link.Commentaires} ({link.Lien})
                        </div>
                    ) : (
                        <div className="text-gray-500 text-xs truncate">{link.Lien}</div>
                    )}
                </div>
            </a>

            <div className="flex items-center gap-2 shrink-0">
                <TypeBadge type={type} />

                <button
                    type="button"
                    className="hover:bg-gray-50 disabled:opacity-50 ml-1 px-2 py-2 border rounded-md text-xs"
                    onClick={async () => {
                        try {
                            await navigator.clipboard.writeText(ensureHttp(link.Lien));
                        } catch { }
                    }}
                    title="Copier l’URL"
                    disabled={busy}
                >
                    <i className="fa-regular fa-copy" />
                </button>

                {onEdit && (
                    <button
                        type="button"
                        className="hover:bg-gray-50 disabled:opacity-50 px-2 py-2 border rounded-md text-xs"
                        onClick={onEdit}
                        title="Modifier"
                        disabled={busy}
                    >
                        <i className="fa-pen-to-square fa-regular" />
                    </button>
                )}
                {onDelete && (
                    <button
                        type="button"
                        className="hover:bg-gray-50 disabled:opacity-50 px-2 py-2 border rounded-md text-red-600 text-xs"
                        onClick={onDelete}
                        title="Supprimer"
                        disabled={busy}
                    >
                        <i className="fa-regular fa-trash-can" />
                    </button>
                )}
            </div>
        </div>
    );
}

function TypeBadge({ type }) {
    const map = {
        pdf: { label: "PDF", icon: "fa-file-pdf", cls: "bg-red-50 text-red-700 border-red-200" },
        sheet: { label: "XLS/CSV", icon: "fa-file-excel", cls: "bg-green-50 text-green-700 border-green-200" },
        doc: { label: "DOC", icon: "fa-file-word", cls: "bg-blue-50 text-blue-700 border-blue-200" },
        image: { label: "Image", icon: "fa-image", cls: "bg-purple-50 text-purple-700 border-purple-200" },
        map: { label: "Map", icon: "fa-map", cls: "bg-amber-50 text-amber-700 border-amber-200" },
        drive: { label: "Drive", icon: "fa-cloud", cls: "bg-cyan-50 text-cyan-700 border-cyan-200" },
        web: { label: "Web", icon: "fa-globe", cls: "bg-gray-50 text-gray-700 border-gray-200" },
    };
    const key = normTypeKey(type);
    const cfg = map[key] || map.web;
    return (
        <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${cfg.cls}`}>
            <i className={`fa-solid ${cfg.icon}`} />
            {cfg.label}
        </span>
    );
}

/* ---------------- utils ---------------- */

function blankForm() {
    return {
        UUID_: null,
        UUID_Externe: null,
        Nom_Lien: "",
        Lien: "",
        Commentaires: "",
        Partage: false,
        Origine: "",
        Type_Lien: "web",
        Creation: null,
    };
}

function toUI(db) {
    return {
        id: genId(),
        UUID_: db.UUID_ ?? null,
        UUID_Externe: db.UUID_Externe ?? null,
        Nom_Lien: db.Nom_Lien || "",
        Lien: ensureHttp(db.Lien),
        Commentaires: db.Commentaires || "",
        Partage: !!db.Partage,
        Origine: db.Origine || "",
        Type_Lien: normTypeKey(db.Type_Lien || guessType(db.Lien)),
        Creation: db.Creation || null,
    };
}

function toObjectsDB(list) {
    return (list || [])
        .map((l) => {
            if (typeof l === "string") {
                return {
                    id: genId(),
                    UUID_: null,
                    UUID_Externe: null,
                    Nom_Lien: getDomain(l) || l,
                    Lien: ensureHttp(l),
                    Commentaires: "",
                    Partage: false,
                    Origine: "",
                    Type_Lien: guessType(l),
                    Creation: null,
                };
            }
            if (!("Lien" in l) && ("url" in l || "label" in l || "description" in l)) {
                return {
                    id: l.id || genId(),
                    UUID_: l.UUID_ ?? null,
                    UUID_Externe: l.UUID_Externe ?? null,
                    Nom_Lien: l.label || getDomain(l.url) || l.url || "",
                    Lien: ensureHttp(l.url),
                    Commentaires: l.description || "",
                    Partage: !!l.Partage,
                    Origine: l.Origine || "",
                    Type_Lien: l.Type_Lien || guessType(l.url),
                    Creation: l.Creation || null,
                };
            }
            return toUI(l);
        })
        .filter((l) => !!l.Lien);
}

function toDB(list) { return list.map(toDB1); }
function toDB1(l) {
    return {
        UUID_: l.UUID_ ?? null,
        UUID_Externe: l.UUID_Externe ?? null,
        Nom_Lien: l.Nom_Lien ?? "",
        Lien: ensureHttp(l.Lien ?? ""),
        Commentaires: l.Commentaires ?? "",
        Partage: !!l.Partage,
        Origine: l.Origine ?? "",
        Type_Lien: normTypeKey(l.Type_Lien ?? ""),
        Creation: l.Creation ?? null,
    };
}

function ensureHttp(u) { if (!u) return ""; return /^https?:\/\//i.test(u) ? u : `https://${u}`; }
function getDomain(u) { try { return new URL(ensureHttp(u)).hostname.replace(/^www\./, ""); } catch { return ""; } }
function guessType(url) {
    const u = (url || "").toLowerCase();
    if (u.includes("google.com/maps")) return "map";
    if (u.includes("drive.google.com")) return "drive";
    if (/\.(pdf)(\?|$)/.test(u)) return "pdf";
    if (/\.(xlsx?|csv)(\?|$)/.test(u)) return "sheet";
    if (/\.(docx?)(\?|$)/.test(u)) return "doc";
    if (/\.(png|jpe?g|webp|gif|svg)(\?|$)/.test(u)) return "image";
    return "web";
}
function normTypeKey(t) {
    const x = (t || "").toLowerCase();
    if (["xls", "xlsx", "csv", "sheet"].includes(x)) return "sheet";
    if (["doc", "docx", "word"].includes(x)) return "doc";
    if (["jpg", "jpeg", "png", "gif", "svg", "image", "img", "webp"].includes(x)) return "image";
    return x || "web";
}
function genId() { return `l_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`; }

function normOri(s) {
    return (s || "")
        .toString()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
}

function BarSkeleton() {
    return (
        <div className="flex items-center gap-2 mb-3">
            {/* search input */}
            <div className="flex-1">
                <div className="bg-gray-200 rounded-md h-9 animate-pulse" />
            </div>
            {/* bouton ajouter */}
            <div className="bg-gray-200 rounded-lg w-28 h-9 animate-pulse" />
        </div>
    );
}

function LinksSkeleton({ rows = 3 }) {
    return (
        <ul className="divide-y">
            {Array.from({ length: rows }).map((_, i) => (
                <li key={i} className="py-2">
                    <div className="flex justify-between items-center gap-3">
                        <div className="flex items-center gap-3 ml-3 min-w-0">
                            {/* favicon */}
                            <div className="bg-gray-200 rounded-sm w-5 h-5 animate-pulse" />
                            <div className="flex-1 min-w-0">
                                {/* titre */}
                                <div className="bg-gray-200 rounded-md w-40 h-4 animate-pulse" />
                                {/* sous-titre / url */}
                                <div className="bg-gray-200 mt-1 rounded-md w-64 h-3 animate-pulse" />
                            </div>
                        </div>
                        {/* badges + actions */}
                        <div className="flex items-center gap-2 shrink-0">
                            <div className="bg-gray-200 rounded-md w-14 h-6 animate-pulse" />
                            <div className="bg-gray-200 rounded-md w-8 h-8 animate-pulse" />
                            <div className="bg-gray-200 rounded-md w-8 h-8 animate-pulse" />
                            <div className="bg-gray-200 rounded-md w-8 h-8 animate-pulse" />
                        </div>
                    </div>
                </li>
            ))}
        </ul>
    );
}
